// Default kettlebell workouts. Our own plans; "inspired by" names the idea, no text is copied.
// Ideas from the owner's draft in the git history of docs/starter-workouts.md (2026-10-11).
import type { Workout } from '../types';
import { ids, reps, t } from './plan';

export const KETTLEBELL_WORKOUTS: Workout[] = [
  {
    id: 'kettlebell_classic_20',
    name: 'Kettlebell Classic 20',
    description: 'A classic EMOM: every minute on the minute for 20 minutes, four moves in rotation. Rest for what is left of each minute.',
    level: 'intermediate',
    focus: 'full_body',
    equipment: ['kettlebell'],
    xp: 60,
    source: { label: 'Our own' },
    warmupId: 'kettlebell',
    cooldownId: 'full_body',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'emom', minutes: 20 }, blocks: ids('main', [
        { exerciseId: 'kettlebell_swing', amount: reps(15) },
        { exerciseId: 'goblet_squat', amount: reps(10) },
        { exerciseId: 'push_up', amount: reps(10) },
        { exerciseId: 'plank', amount: t(40) },
      ]) },
    ],
  },
  {
    id: 'kettlebell_flow_20',
    name: 'Kettlebell Flow 20',
    description: 'Twenty minutes non-stop with one bell: a flow of five moves, repeated at a steady pace. Set the bell down only when form slips.',
    level: 'intermediate',
    focus: 'conditioning',
    equipment: ['kettlebell'],
    xp: 65,
    source: { label: 'Our own' },
    warmupId: 'kettlebell',
    cooldownId: 'full_body',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'continuous', minutes: 20 }, blocks: ids('main', [
        { exerciseId: 'kettlebell_swing', amount: reps(10) },
        { exerciseId: 'alternating_clean', amount: reps(5, { perSide: true }) },
        { exerciseId: 'kettlebell_press', amount: reps(5, { perSide: true }) },
        { exerciseId: 'goblet_reverse_lunge', amount: reps(5, { perSide: true }) },
        { exerciseId: 'kettlebell_halo', amount: reps(5, { perSide: true }), note: 'per direction' },
      ]) },
    ],
  },
  {
    id: 'kettlebell_swing_20',
    name: 'Swing 20 Non-Stop',
    description: 'Twenty minutes of swings without a planned break: two hands, one hand, hand to hand. Pick a bell you can swing for a minute with ease.',
    level: 'intermediate',
    focus: 'conditioning',
    equipment: ['kettlebell'],
    xp: 70,
    source: { label: 'Our own' },
    warmupId: 'kettlebell',
    cooldownId: 'legs_hips',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'continuous', minutes: 20 }, blocks: ids('main', [
        { exerciseId: 'kettlebell_swing', amount: t(60) },
        { exerciseId: 'one_arm_swing', amount: t(30, true) },
        { exerciseId: 'hand_to_hand_swing', amount: t(60) },
      ]) },
    ],
  },
  {
    id: 'kettlebell_complex',
    name: 'Kettlebell Complex',
    description: 'Four moves in a row without setting the bell down, first on one arm, then the other. Five rounds, rest 90 seconds between them.',
    level: 'advanced',
    focus: 'full_body',
    equipment: ['kettlebell'],
    xp: 60,
    source: { label: 'Our own' },
    warmupId: 'kettlebell',
    cooldownId: 'upper_back',
    sections: [
      // One arm through all four moves, then the other arm: one block per move and arm, in that order.
      // Rep amounts on every block, no rest between them: the circuit's workSec is not used here.
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 5, workSec: 30, restSec: 0, roundRestSec: 90 }, blocks: ids('main', [
        { exerciseId: 'kettlebell_clean', amount: reps(5), note: 'left arm' },
        { exerciseId: 'kettlebell_press', amount: reps(5), note: 'left arm' },
        { exerciseId: 'kettlebell_front_squat', amount: reps(5), note: 'left arm' },
        { exerciseId: 'half_snatch', amount: reps(5), note: 'left arm' },
        { exerciseId: 'kettlebell_clean', amount: reps(5), note: 'right arm' },
        { exerciseId: 'kettlebell_press', amount: reps(5), note: 'right arm' },
        { exerciseId: 'kettlebell_front_squat', amount: reps(5), note: 'right arm' },
        { exerciseId: 'half_snatch', amount: reps(5), note: 'right arm' },
      ]) },
    ],
  },
  {
    id: 'swings_and_get_ups',
    name: 'Swings and Get-ups',
    description: 'Ten sets of ten one-arm swings, then ten Turkish get-ups, one each minute. Simple, daily-practice style; add weight only when it feels easy.',
    level: 'intermediate',
    focus: 'strength',
    equipment: ['kettlebell'],
    xp: 55,
    source: { label: 'Inspired by the Simple and Sinister program (Pavel Tsatsouline); our own plan and wording' },
    warmupId: 'kettlebell',
    cooldownId: 'full_body',
    sections: [
      { id: 'swings', kind: 'main', format: { kind: 'emom', minutes: 10 }, blocks: ids('swings', [
        { exerciseId: 'one_arm_swing', amount: reps(10), note: 'switch hands each minute' },
      ]) },
      { id: 'get_ups', kind: 'main', format: { kind: 'emom', minutes: 10 }, blocks: ids('get_ups', [
        { exerciseId: 'turkish_get_up', amount: reps(1), note: 'switch sides each minute' },
      ]) },
    ],
  },
  {
    id: 'kettlebell_strength_30',
    name: 'Kettlebell Strength 30',
    description: 'Heavier bell, fewer reps, full rests: clean and press, front squat, row and Romanian deadlift in sets.',
    level: 'intermediate',
    focus: 'strength',
    equipment: ['kettlebell'],
    xp: 70,
    source: { label: 'Our own' },
    warmupId: 'kettlebell',
    cooldownId: 'upper_back',
    sections: [
      { id: 'press', kind: 'main', format: { kind: 'sets', sets: 4, restSec: 90 }, blocks: ids('press', [
        { exerciseId: 'clean_and_press', amount: reps(5, { perSide: true }) },
      ]) },
      { id: 'squat', kind: 'main', format: { kind: 'sets', sets: 4, restSec: 90 }, blocks: ids('squat', [
        { exerciseId: 'kettlebell_front_squat', amount: reps(6, { perSide: true }) },
      ]) },
      { id: 'accessory', kind: 'main', format: { kind: 'sets', sets: 3, restSec: 60 }, blocks: ids('accessory', [
        { exerciseId: 'kettlebell_row', amount: reps(10, { perSide: true }) },
        { exerciseId: 'kettlebell_rdl', amount: reps(12) },
      ]) },
    ],
  },
  {
    id: 'kettlebell_hiit_15',
    name: 'Kettlebell HIIT 15',
    description: 'Fifteen minutes of intervals, 40 seconds on and 20 off, five moves for three rounds. Short and hard.',
    level: 'intermediate',
    focus: 'conditioning',
    equipment: ['kettlebell'],
    xp: 45,
    source: { label: 'Our own' },
    warmupId: 'kettlebell',
    cooldownId: 'full_body',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 3, workSec: 40, restSec: 20 }, blocks: ids('main', [
        { exerciseId: 'kettlebell_swing' },
        { exerciseId: 'goblet_squat' },
        { exerciseId: 'sumo_high_pull' },
        { exerciseId: 'mountain_climber' },
        { exerciseId: 'kettlebell_rdl' },
      ]) },
    ],
  },
  {
    id: 'upper_body_30',
    name: 'Upper Body 30',
    description: 'Push-ups, rows and presses with one kettlebell, in sets with rest. Arms, shoulders, chest and back.',
    level: 'intermediate',
    focus: 'upper_body',
    equipment: ['kettlebell'],
    xp: 55,
    source: { label: 'Our own' },
    warmupId: 'kettlebell',
    cooldownId: 'upper_back',
    sections: [
      { id: 'push', kind: 'main', format: { kind: 'sets', sets: 4, restSec: 60 }, blocks: ids('push', [
        { exerciseId: 'push_up', amount: reps(8, { max: 15 }), note: 'as many as you can with good form' },
      ]) },
      { id: 'pull_press', kind: 'main', format: { kind: 'sets', sets: 3, restSec: 60 }, blocks: ids('pull_press', [
        { exerciseId: 'kettlebell_row', amount: reps(10, { perSide: true }) },
        { exerciseId: 'kettlebell_press', amount: reps(8, { perSide: true }) },
      ]) },
      { id: 'finisher', kind: 'main', format: { kind: 'sets', sets: 2, restSec: 45 }, blocks: ids('finisher', [
        { exerciseId: 'kettlebell_halo', amount: reps(8, { perSide: true }), note: 'per direction' },
        { exerciseId: 'diamond_push_up', amount: reps(6, { max: 12 }) },
      ]) },
    ],
  },
  {
    id: 'legs_30',
    name: 'Legs 30',
    description: 'Squat, lunge, hinge and bridge with one kettlebell, then calves and a wall sit to finish.',
    level: 'beginner',
    focus: 'lower_body',
    equipment: ['kettlebell'],
    xp: 55,
    source: { label: 'Our own' },
    warmupId: 'kettlebell',
    cooldownId: 'legs_hips',
    sections: [
      { id: 'squat', kind: 'main', format: { kind: 'sets', sets: 4, restSec: 75 }, blocks: ids('squat', [
        { exerciseId: 'goblet_squat', amount: reps(12) },
      ]) },
      { id: 'strength', kind: 'main', format: { kind: 'sets', sets: 3, restSec: 45 }, blocks: ids('strength', [
        { exerciseId: 'goblet_reverse_lunge', amount: reps(10, { perSide: true }) },
        { exerciseId: 'kettlebell_rdl', amount: reps(12) },
        { exerciseId: 'glute_bridge', amount: reps(15) },
        { exerciseId: 'calf_raise', amount: reps(15) },
      ]) },
      { id: 'finisher', kind: 'main', format: { kind: 'sets', sets: 2, restSec: 45 }, blocks: ids('finisher', [
        { exerciseId: 'wall_sit', amount: t(45) },
      ]) },
    ],
  },
  {
    id: 'hiit_20',
    name: 'HIIT 20',
    description: 'Burpees, jacks, swings, climbers and squat jumps: 40 seconds on, 20 off, four rounds.',
    level: 'intermediate',
    focus: 'conditioning',
    equipment: ['kettlebell'],
    xp: 65,
    source: { label: 'Our own' },
    warmupId: 'general',
    cooldownId: 'full_body',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 4, workSec: 40, restSec: 20 }, blocks: ids('main', [
        { exerciseId: 'burpee' },
        { exerciseId: 'jumping_jack' },
        { exerciseId: 'kettlebell_swing' },
        { exerciseId: 'mountain_climber' },
        { exerciseId: 'squat_jump' },
      ]) },
    ],
  },
];
